import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { usePlanManagerContext } from "@/providers/PlanManagerProvider";
import { usePlanningCache } from "@/appComponents/Navigation/RightSidebar/Planning/usePlanningCache";
import { ChangeDisplayRow } from "@/appComponents/Blocks/Composites/ChangeDisplayRow/ChangeDisplayRow";
import { getRawPreviousFieldValue } from "@/appComponents/Blocks/Composites/ChangeDisplayRow/ChangeDisplayRow.utils";
import {
  enumObjClassNames,
  enumObjectTypes,
  enumOverrideType,
  kEmployeePositionID,
  objTypeClassMap
} from "@coreapp/constants";
import { useState, useEffect } from "react";
import { Flex, Skeleton, isNullish } from "@theorgchartdev/storybook";

const SKELETON_MIN_MS = 1500;

/**
 * @param {Object} props
 * @param {import("@coreapp/CoreApp/QChange").QChange | null} props.change
 * @param {number} props.changeIndex
 * @param {((overflow: boolean) => void)} [props.onOverflow]
 * @param {boolean} [props.showCompact]
 * @param {boolean} [props.showCommentBubble]
 * @param {import("@coreapp/CoreApp/QChange").QChange[]} [props.allChanges]
 */
export const PlanningChangeDisplayRow = ({
  change,
  changeIndex,
  onOverflow = _v => {},
  showCompact = false,
  showCommentBubble = false,
  allChanges
}) => {
  const { planManager } = usePlanManagerContext();
  const planMgr = planManager.state;

  const override = change.fParentOverride;
  const changeType = override.SmartGetFieldValue("Type");
  const objType = override.SmartGetFieldValue("objType") ?? enumObjectTypes.holder;
  const recordID = override.SmartGetFieldValue("RefID");

  const doc = GetDoc();
  const objDB = doc.GetObjDB();
  const objClassName = objTypeClassMap[objType];
  const objClass = objDB.GetClassByName(objClassName);

  const [parentFieldDef, parentObjType] = (() => {
    if (doc.GetHierarchyObjectType() === objType) {
      return [objClass.GetSupervisorIDFieldDef(), objType];
    }

    if (doc.IsPositionDocument() && objType === enumObjectTypes.holder) {
      return [objClass.GetFieldDefByID(kEmployeePositionID), enumObjectTypes.position];
    }

    return [null, null];
  })();

  const wsMgr = GetWSMgr();
  const recordDBObj = planMgr.GetCacheBaseRecord(objType, recordID);
  const cacheRef =
    (objType === enumObjectTypes.position
      ? planMgr.fPositionCloneBaseCache
      : planMgr.fCloneBaseCache) ?? {};
  const loadComplete = Object.prototype.hasOwnProperty.call(cacheRef, recordID);

  const originalParentID = parentFieldDef
    ? getRawPreviousFieldValue(parentFieldDef, { override, change, wsMgr, objType })
    : null;

  let currentParentID = change.GetCurrentValue(parentFieldDef);
  if (isNullish(currentParentID)) {
    currentParentID = originalParentID;
    const parentIDFieldName = parentFieldDef?.GetFieldName();
    if (parentIDFieldName && change.fDelta[parentIDFieldName]) {
      currentParentID = change.fDelta[parentIDFieldName];
    }
  }

  let fetchIds = [{ refId: recordID, objType: objType }];

  if (parentObjType) {
    if (currentParentID) {
      fetchIds.push({ refId: currentParentID, objType: parentObjType });
    }

    if (originalParentID && originalParentID !== currentParentID) {
      fetchIds.push({ refId: originalParentID, objType: parentObjType });
    }
  }

  usePlanningCache(fetchIds);

  const isAdd = changeType === enumOverrideType.add;
  const [minTimeElapsed, setMinTimeElapsed] = useState(!!recordDBObj || isAdd);

  useEffect(() => {
    if (minTimeElapsed) return;
    const timer = setTimeout(() => setMinTimeElapsed(true), SKELETON_MIN_MS);
    return () => clearTimeout(timer);
  }, [minTimeElapsed]);

  const parentLoadComplete =
    (!currentParentID || planMgr.HasCacheEntry(parentObjType, currentParentID)) &&
    (!originalParentID || planMgr.HasCacheEntry(parentObjType, originalParentID));

  const showSkeleton =
    (!recordDBObj && !isAdd && !loadComplete) || !parentLoadComplete || !minTimeElapsed;

  if (showSkeleton) {
    return (
      <Flex gap={"4px"} alignItems={"flex-start"} width={"100%"}>
        <Skeleton animation="pulsate" height={16} variant="text" width={100} widthUnits="%" />
      </Flex>
    );
  }

  const superDBObj = planMgr.GetCacheBaseRecord(parentObjType, currentParentID) ?? null;
  const origDBObj = planMgr.GetCacheBaseRecord(parentObjType, originalParentID) ?? null;

  return (
    <ChangeDisplayRow
      change={change}
      currentParentDBObj={superDBObj}
      originalParentDBObj={origDBObj}
      recordDBObj={recordDBObj}
      onOverflow={onOverflow}
      showCompact={showCompact}
      showComment={showCommentBubble}
      showAdditionalChanges={changeType === enumOverrideType.add ? changeIndex > 0 : true}
      allChanges={allChanges}
      doc={doc}
      wsMgr={wsMgr}
    />
  );
};
